import React from 'react';
import {
  StyleSheet,
  View,
  Alert,
} from 'react-native';
import { ExcelUploadComponent } from '../../components/upload/ExcelUploadComponent';
import { supabase } from '../../api/supabase';

const DriverExcelUploadScreen: React.FC = () => {

  // Expected column mappings for drivers
  const driverColumnMappings = {
    'Name': 'name',
    'Driver Name': 'name',
    'Full Name': 'name',
    'Phone': 'phone',
    'Phone Number': 'phone',
    'Mobile': 'phone',
    'Mobile Number': 'phone',
    'License Number': 'licenseNumber',
    'Driving License': 'licenseNumber',
    'License': 'licenseNumber',
    'Emirates ID': 'emiratesId',
    'Emirates ID Number': 'emiratesId',
    'ID Number': 'emiratesId',
    'Nationality': 'nationality',
    'Country': 'nationality',
    'Visa Expiry': 'visaExpiry',
    'Visa Date': 'visaExpiry',
    'License Expiry': 'licenseExpiry',
    'License Expiry Date': 'licenseExpiry',
    'Uber UUID': 'uberUuid',
    'Uber ID': 'uberUuid',
    'Careem Captain ID': 'careemCaptainId',
    'Careem ID': 'careemCaptainId',
    'Captain ID': 'careemCaptainId',
    'Status': 'status',
    'Driver Status': 'status',
    'Notes': 'notes',
    'Remarks': 'notes',
    'Comments': 'notes',
    'Address': 'address',
    'Email': 'email',
    'Email Address': 'email',
  };

  const expectedColumns = [
    'name',
    'phone',
    'licenseNumber',
    'emiratesId',
    'nationality',
    'visaExpiry',
    'licenseExpiry',
    'status',
    'notes'
  ];

  const sampleData = {
    'Name': 'Ahmed Ali Mohammed',
    'Phone': '+971501234567',
    'License Number': 'DL12345678',
    'Emirates ID': '784-1234-1234567-1',
    'Nationality': 'Pakistani',
    'Visa Expiry': '2024-12-31',
    'License Expiry': '2025-06-30',
    'Uber UUID': 'uuid-12345',
    'Careem Captain ID': 'captain-67890',
    'Status': 'active',
    'Notes': 'Experienced driver'
  };

  const processDriverData = async (data: any[]) => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const [index, row] of data.entries()) {
      try {
        // Validate required fields
        if (!row.name) {
          throw new Error('Driver name is required');
        }

        if (!row.phone) {
          throw new Error('Phone number is required');
        }

        // Check for duplicates by phone number
        const { data: existingDriver } = await supabase
          .from('drivers')
          .select('id')
          .eq('phone', row.phone)
          .single();

        if (existingDriver) {
          // Update existing driver
          await updateExistingDriver(existingDriver.id, row);
          successCount++;
        } else {
          // Create new driver
          await createNewDriver(row);
          successCount++;
        }

      } catch (error) {
        errorCount++;
        errors.push(`Row ${row._rowNumber || index + 1}: ${error.message}`);
      }
    }

    // Show results
    if (errorCount > 0) {
      Alert.alert(
        'Upload Completed with Errors',
        `Successfully processed: ${successCount}\nErrors: ${errorCount}\n\nFirst few errors:\n${errors.slice(0, 5).join('\n')}`,
        [{ text: 'OK' }]
      );
    } else {
      Alert.alert(
        'Upload Successful',
        `Successfully processed ${successCount} drivers!`,
        [{ text: 'OK' }]
      );
    }
  };

  const createNewDriver = async (driverData: any) => {
    // Prepare driver data for database
    const dbDriverData = {
      name: driverData.name,
      phone: driverData.phone,
      license_number: driverData.licenseNumber || null,
      emirates_id: driverData.emiratesId || null,
      nationality: driverData.nationality || null,
      visa_expiry: parseDate(driverData.visaExpiry),
      license_expiry: parseDate(driverData.licenseExpiry),
      uber_uuid: driverData.uberUuid || null,
      careem_captain_id: driverData.careemCaptainId || null,
      status: normalizeDriverStatus(driverData.status) || 'active',
      notes: driverData.notes || null,
      address: driverData.address || null,
      email: driverData.email || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Insert into database
    const { data, error } = await supabase
      .from('drivers')
      .insert(dbDriverData)
      .select()
      .single();

    if (error) {
      throw new Error(`Database error: ${error.message}`);
    }

    return data;
  };

  const updateExistingDriver = async (driverId: string, driverData: any) => {
    const updateData: any = {};

    // Only update fields that have values
    if (driverData.name) updateData.name = driverData.name;
    if (driverData.phone) updateData.phone = driverData.phone;
    if (driverData.licenseNumber) updateData.license_number = driverData.licenseNumber;
    if (driverData.emiratesId) updateData.emirates_id = driverData.emiratesId;
    if (driverData.nationality) updateData.nationality = driverData.nationality;
    if (driverData.visaExpiry) updateData.visa_expiry = parseDate(driverData.visaExpiry);
    if (driverData.licenseExpiry) updateData.license_expiry = parseDate(driverData.licenseExpiry);
    if (driverData.uberUuid) updateData.uber_uuid = driverData.uberUuid;
    if (driverData.careemCaptainId) updateData.careem_captain_id = driverData.careemCaptainId;
    if (driverData.status) updateData.status = normalizeDriverStatus(driverData.status);
    if (driverData.notes) updateData.notes = driverData.notes;
    if (driverData.address) updateData.address = driverData.address;
    if (driverData.email) updateData.email = driverData.email;

    updateData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('drivers')
      .update(updateData)
      .eq('id', driverId);

    if (error) {
      throw new Error(`Database update error: ${error.message}`);
    }
  };

  const normalizeDriverStatus = (status: string): string => {
    if (!status) return 'active';
    
    const statusLower = status.toLowerCase().trim();
    
    if (statusLower.includes('active') || statusLower.includes('working')) return 'active';
    if (statusLower.includes('inactive') || statusLower.includes('stopped')) return 'inactive';
    if (statusLower.includes('suspended') || statusLower.includes('banned')) return 'suspended';
    if (statusLower.includes('pending') || statusLower.includes('review')) return 'pending';
    
    return 'active'; // Default
  };

  const parseDate = (dateString: string): string | null => {
    if (!dateString) return null;
    
    try {
      // Handle Excel date serial numbers
      if (/^\d{5}$/.test(dateString)) {
        const excelDate = new Date((parseInt(dateString) - 25569) * 86400 * 1000);
        return excelDate.toISOString().split('T')[0];
      }
      
      // Handle various date formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      
      return date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  return (
    <View style={styles.container}>
      <ExcelUploadComponent
        title="Driver Excel Upload"
        description="Upload driver data from Excel files (.xlsx, .xls) or CSV files. The system will automatically map columns and handle duplicates based on phone numbers."
        expectedColumns={expectedColumns}
        columnMappings={driverColumnMappings}
        onDataProcessed={processDriverData}
        sampleData={sampleData}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
});

export default DriverExcelUploadScreen;
